import {
    SET_ANALYSIS_PROFILE,
    START_ANALYSIS_REQUEST,
    START_ANALYSIS_SUCCESS,
    START_ANALYSIS_FAILURE,
    FETCH_ANALYSIS_REQUEST,
    FETCH_ANALYSIS_SUCCESS,
    FETCH_ANALYSIS_FAILURE,
    PING_ANALYSIS_REQUEST,
    PING_ANALYSIS_SUCCESS,
    PING_ANALYSIS_FAILURE,
    CANCEL_ANALYSIS_REQUEST,
    CANCEL_ANALYSIS_SUCCESS,
    CANCEL_ANALYSIS_FAILURE,
    SET_ORDER_BY,
    SET_ORDER,
    SET_FEATURE_SELECTED,
    SET_ALL_FEATURES_SELECTED,
    CLEAR_ANALYSIS,
    EXPORT_ANALYSIS_REQUEST,
    EXPORT_ANALYSIS_SUCCESS,
    EXPORT_ANALYSIS_FAILURE,
    FETCH_ANALYSES_REQUEST,
    FETCH_ANALYSES_SUCCESS,
    FETCH_ANALYSES_FAILURE
} from './Types';

import axios, { CancelTokenSource } from 'axios';
import _, { debounce } from 'lodash';
import { addDrawFeature, setFitBoundsFromGeojson } from '../map/Actions';
import { MAIN_MAP } from '../map/Constants';
import { fetchSearchSuccess } from '../search/Actions';
import { Analysis } from './analysisTypes';

const CancelToken = axios.CancelToken;
let startAnalysisSource: CancelTokenSource, fetchAnalysisSource: CancelTokenSource, pingAnalysisSource: CancelTokenSource | null;

export const setAnalysisProfile = (id: number) => ({ type: SET_ANALYSIS_PROFILE, id });
const startAnalysis = () => ({ type: START_ANALYSIS_REQUEST });
const startAnalysisSuccess = (analysis: Analysis) => ({ type: START_ANALYSIS_SUCCESS, analysis });
const startAnalysisError = (error: any) => ({ type: START_ANALYSIS_FAILURE, error });
const fetchAnalysis = (id?: number) => ({ type: FETCH_ANALYSIS_REQUEST, id });
const fetchAnalysisSuccess = (analysis: Analysis) => ({ type: FETCH_ANALYSIS_SUCCESS, analysis });
const fetchAnalysisError = (id: number, error?: any) => ({ type: FETCH_ANALYSIS_FAILURE, id, error });
const pingAnalysis = () => ({ type: PING_ANALYSIS_REQUEST });
const pingAnalysisSuccess = (analysis: Analysis) => ({ type: PING_ANALYSIS_SUCCESS, analysis });
const pingAnalysisError = (error: any) => ({ type: PING_ANALYSIS_FAILURE, error });
const cancelAnalysis = () => ({ type: CANCEL_ANALYSIS_REQUEST });
const cancelAnalysisSuccess = () => ({ type: CANCEL_ANALYSIS_SUCCESS });
const cancelAnalysisError = (error: any) => ({ type: CANCEL_ANALYSIS_FAILURE, error });
export const setOrderBy = (orderBy: number) => ({ type: SET_ORDER_BY, orderBy });
export const setOrder = (order: number) => ({ type: SET_ORDER, order });
export const setFeatureSelected = (id: number, isSelected: boolean) => ({ type: SET_FEATURE_SELECTED, id, isSelected });
export const setAllFeaturesSelected = (isSelected: boolean) => ({ type: SET_ALL_FEATURES_SELECTED, isSelected });
export const clearAnalysis = () => ({ type: CLEAR_ANALYSIS });
const exportAnalysis = () => ({ type: EXPORT_ANALYSIS_REQUEST });
const exportAnalysisSuccess = () => ({ type: EXPORT_ANALYSIS_SUCCESS });
const exportAnalysisError = (error: any) => ({ type: EXPORT_ANALYSIS_FAILURE, error });
const fetchAnalyses = () => ({ type: FETCH_ANALYSES_REQUEST });
const fetchAnalysesSuccess = (analyses: Analysis[]) => ({ type: FETCH_ANALYSES_SUCCESS, analyses });
const fetchAnalysesError = (error: any) => ({ type: FETCH_ANALYSES_FAILURE, error });

const ping = (dispatch: (arg0: { type: string; analysis?: Analysis; error?: any; }) => void, id: number) => {
    pingAnalysisSource && pingAnalysisSource.cancel('Cancelling already running request');

    dispatch(pingAnalysis());
    return axios.get(`/api/result_sets/${id}`, {
        cancelToken: (pingAnalysisSource = axios.CancelToken.source()).token
    }).then(response => {
        pingAnalysisSource = null;
        dispatch(pingAnalysisSuccess(response.data));
        // If still running then ping again
        if (response.data.status === 'ready' || response.data.status === 'queued' || response.data.status === 'running') {
            debouncedPing(dispatch, response.data.id);
        }
    }).catch(error => {
        pingAnalysisSource = null;
        if (axios.isCancel(error)) {
            console.log('cancel', error);
        } else {
            dispatch(pingAnalysisError(error.response));
            console.error(error);
        }
    });
};

const debouncedPing = debounce(ping, 1000);

export const startAnalysisAsync = (projectId: number, searchId: number, weightingProfileId: number) => {
    return (dispatch: (arg0: { type: string; analysis?: Analysis | undefined; error?: any; }) => void) => {
        cancelRequests();

        dispatch(startAnalysis());
        return axios.post(`/api/projects/${projectId}/result_sets`, {
            search_id: searchId,
            weighting_profile_id: weightingProfileId
        }, {
            cancelToken: (startAnalysisSource = axios.CancelToken.source()).token
        }).then(response => {
            dispatch(startAnalysisSuccess(response.data));
            debouncedPing(dispatch, response.data.id);
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(startAnalysisError(error.response));
                console.error(error);
            }
        });
    };
};

// This function assumes that once the analysis loads the user wants to go to and show on the map
export const fetchAnalysisAsync = (id: number) => {
    return (dispatch: any) => {
        cancelRequests();

        dispatch(fetchAnalysis());
        return axios.get(`/api/result_sets/${id}`, {
            cancelToken: (fetchAnalysisSource = axios.CancelToken.source()).token
        }).then(response => {
            dispatch(fetchSearchSuccess(response.data.search));
            if (response.data.search.areaFacet.layerType === 'drawn') { dispatch(addDrawFeature(_.cloneDeep(response.data.search.area), MAIN_MAP)); }
            if (response.data.search.bounds) { dispatch(setFitBoundsFromGeojson(response.data.search.bounds, { maxZoom: 16 }, MAIN_MAP)); }

            // Should remove search from analysis as isn't needed there, maybe?
            dispatch(fetchAnalysisSuccess(response.data));
            if (['ready', 'queued', 'running'].includes(response.data.status)) {
                debouncedPing(dispatch, response.data.id);
            }
        }).catch(error => {
            if (axios.isCancel(error)) {
                console.log('cancel');
            } else {
                dispatch(fetchAnalysisError(error.response));
                console.error(error);
            }
        });
    };
};

export const cancelAnalysisAsync = (id: number) => {
    return (dispatch: (arg0: { type: string; error?: any; }) => void) => {
        cancelRequests();

        dispatch(cancelAnalysis());
        return axios.patch(`/api/result_sets/${id}/cancel`, {}, {
            // cancelToken: pingAnalysisSource.token
        }).then(response => {
            dispatch(cancelAnalysisSuccess());
        }).catch(error => {
            dispatch(cancelAnalysisError(error.response));
            console.error(error);
        });
    };
};

const cancelRequests = () => {
    debouncedPing.cancel();
    pingAnalysisSource && pingAnalysisSource.cancel('Operation cancelled by user');
    startAnalysisSource && startAnalysisSource.cancel('Cancelling already running request');
    fetchAnalysisSource && fetchAnalysisSource.cancel('Cancelling already running request');
};

export const exportAnalysesAsync = (id: number, featureAnalysesIds: number[]) => {
    return (dispatch: (arg0: { type: string; error?: any; }) => void) => {
        dispatch(exportAnalysis());
        return axios.post(`/api/result_sets/${id}/export`, {
            ids: featureAnalysesIds
        }).then(response => {
            // Download Begin
            // Apparently this is a standard way to download a file directly, whether this should be done from Redux? Probably not
            // "Proper" way would be to use standard href to download but also add on click functionality that routinely checks request
            // to see if it has finished
            const filenameRegex = /filename="?([^"]+)"?/;
            const filename = response.headers['content-disposition'].match(filenameRegex)[1];
            const element = document.createElement('a');
            element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(response.data));
            element.setAttribute('download', filename);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            // Download End
            dispatch(exportAnalysisSuccess());
        }).catch(error => {
            dispatch(exportAnalysisError(error.response));
            console.error(error.response);
        });
    };
};

export const fetchAnalysesAsync = (projectId: number) => {
    return (dispatch: (arg0: { type: string; analyses?: Analysis[]; error?: any; }) => void) => {
        dispatch(fetchAnalyses());
        return axios.get(`/api/projects/${projectId}/result_sets`, {

        }).then(response => {
            dispatch(fetchAnalysesSuccess(response.data));
        }).catch(error => {
            dispatch(fetchAnalysesError(error.response));
            console.error(error.response);
        });
    };
};